import type { MeResponse } from '@tabel/shared/schemas/auth';
import { type AppRole, isAppRole, isObjectId } from './role';

/** Объект в переключателе объекта (007 T022): объект, где у пользователя есть роль. */
export interface UserObject {
  id: string;
  name: string;
  /** Роли пользователя на объекте, порядок — `APP_ROLES`; `null` — сервер ролей не прислал. */
  roles: readonly AppRole[] | null;
}

function parse(v: unknown): UserObject | null {
  if (typeof v !== 'object' || v === null) return null;
  const o = v as { id?: unknown; name?: unknown; roles?: unknown };
  if (!isObjectId(o.id) || typeof o.name !== 'string') return null;
  const roles = Array.isArray(o.roles) ? o.roles.filter(isAppRole) : null;
  return { id: o.id, name: o.name, roles };
}

/**
 * Объекты пользователя для переключателя (research R18: «объекты, где у пользователя есть роль»). Список — поле
 * `objects` ответа `/api/me` (`{ id, name, roles }[]`). В схеме `MeResponse` его пока нет: его добавит сервер (отчёт
 * T022). До него и при пустом списке — один объект ответа (`me.object`) с ролями `me.user.roles`. Текущий объект есть
 * в списке всегда; повторы и записи не той формы отбрасываются.
 */
export function userObjects(me: MeResponse): UserObject[] {
  const raw = (me as MeResponse & { objects?: unknown }).objects;
  const out: UserObject[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const o = parse(item);
    if (o && !out.some((x) => x.id === o.id)) out.push(o);
  }
  if (!out.some((o) => o.id === me.object.id))
    out.unshift({ id: me.object.id, name: me.object.name, roles: me.user.roles });
  return out;
}

/** Роль на другом объекте: текущая, если она там есть (или роли неизвестны), иначе первая роль объекта. */
export function roleOn(target: UserObject, current: AppRole): AppRole {
  const [first] = target.roles ?? [];
  return first === undefined || target.roles?.includes(current) ? current : first;
}
